<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import { Minus, Plus } from 'lucide-vue-next';
  import { ColoredButton, FormInput, ThemeProvider, UiModalHeader } from '@/shared/ui';

  import { documentManagementMessages } from '../lib/messages';

  const MAX_LOCATIONS = 3;
  const modalTitleId = 'create-physical-locations-modal-title';

  const props = defineProps<{
    title?: string;
    nameErrors?: string[];
    initialNames?: string[];
    isSaving?: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'save', data: { names: string[] }): void;
  }>();

  const resolveInitialNames = (initialNames?: string[]) => {
    const nextNames = initialNames?.slice(0, MAX_LOCATIONS) ?? [];

    return nextNames.length > 0 ? [...nextNames] : [''];
  };

  const names = ref<string[]>(resolveInitialNames(props.initialNames));
  const localNameErrors = ref<string[]>([]);

  const resolvedTitle = computed(
    () => props.title ?? documentManagementMessages.physicalLocations.createModalTitle
  );
  const isFormValid = computed(() => names.value.some((name) => name.trim().length > 0));
  const canAddMoreFields = computed(() => names.value.length < MAX_LOCATIONS);

  const syncNameErrors = (nextErrors?: string[]) => {
    localNameErrors.value = Array.from({ length: names.value.length }, (_, index) => {
      return nextErrors?.[index] ?? '';
    });
  };

  watch(
    () => props.nameErrors,
    (nextErrors) => {
      syncNameErrors(nextErrors);
    },
    { immediate: true, deep: true }
  );

  watch(
    () => props.initialNames,
    (nextInitialNames) => {
      names.value = resolveInitialNames(nextInitialNames);
      syncNameErrors(props.nameErrors);
    },
    { immediate: true, deep: true }
  );

  const handleClose = () => {
    emit('close');
  };

  const handleNameUpdate = (index: number, value: string) => {
    names.value[index] = value;
    localNameErrors.value[index] = '';
  };

  const handleAddField = () => {
    if (!canAddMoreFields.value) {
      return;
    }

    names.value.push('');
    localNameErrors.value.push('');
  };

  const handleRemoveField = (index: number) => {
    if (index === 0) {
      return;
    }

    names.value.splice(index, 1);
    localNameErrors.value.splice(index, 1);
  };

  const handleSave = () => {
    if (!isFormValid.value || props.isSaving) {
      return;
    }

    emit('save', {
      names: names.value.map((name) => name.trim()),
    });
  };

  const handleClosed = () => {
    names.value = resolveInitialNames(props.initialNames);
    syncNameErrors();
  };

  const getNameFieldLabel = (index: number) =>
    documentManagementMessages.physicalLocations.nameInputLabel(index + 1);
</script>

<template>
  <VueFinalModal
    class="flex justify-center items-center"
    content-class="flex flex-col bg-white-01 rounded-2xl w-[500px] shadow-lg"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    @closed="handleClosed"
  >
    <ThemeProvider class="flex flex-col">
      <div
        data-testid="create-physical-locations-modal"
        class="flex flex-col"
      >
        <UiModalHeader
          class="px-6 py-4 text-sm border-b border-gray-02 shrink-0"
          :title="resolvedTitle"
          @close="handleClose"
        />

        <div
          class="px-6 py-6 space-y-4"
          role="group"
          :aria-labelledby="modalTitleId"
        >
          <span
            :id="modalTitleId"
            class="sr-only"
          >{{ resolvedTitle }}</span>

          <div
            v-for="(name, index) in names"
            :key="`physical-location-field-${index}`"
            class="flex items-start gap-3"
          >
            <div class="flex-1">
              <FormInput
                :id="`physical-location-name-${index}`"
                :model-value="name"
                :label="getNameFieldLabel(index)"
                type="text"
                :placeholder="documentManagementMessages.physicalLocations.nameInputPlaceholder"
                label-class="text-sm text-gray-07 font-semibold"
                :max-length="100"
                :error="localNameErrors[index]"
                @update:model-value="handleNameUpdate(index, $event)"
              />

              <p
                v-if="localNameErrors[index]"
                class="sr-only"
                role="alert"
                aria-live="polite"
              >
                {{ localNameErrors[index] }}
              </p>
            </div>

            <button
              v-if="index === 0"
              :data-testid="`create-physical-location-add-${index}`"
              type="button"
              class="mt-[36px] flex items-center justify-center w-6 h-6 rounded bg-[var(--theme-secondary)] text-white hover:opacity-80 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="!canAddMoreFields || isSaving"
              :aria-label="documentManagementMessages.physicalLocations.addFieldAriaLabel"
              @click="handleAddField"
            >
              <Plus class="w-4 h-4" />
            </button>

            <button
              v-else
              :data-testid="`create-physical-location-remove-${index}`"
              type="button"
              class="mt-[36px] flex items-center justify-center w-6 h-6 rounded text-gray-07 bg-gray-08 hover:opacity-80 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="isSaving"
              :aria-label="documentManagementMessages.physicalLocations.removeFieldAriaLabel"
              @click="handleRemoveField(index)"
            >
              <Minus class="w-4 h-4" />
            </button>
          </div>
        </div>

        <div class="px-6 py-8 flex justify-end gap-3 border-t border-gray-02 shrink-0">
          <ColoredButton
            data-testid="create-physical-location-cancel"
            :text="documentManagementMessages.physicalLocations.cancelButtonText"
            outlined
            color="gray-03"
            @click="handleClose"
          />
          <ColoredButton
            data-testid="create-physical-location-save"
            :disabled="!isFormValid || isSaving"
            :text="documentManagementMessages.physicalLocations.saveButtonText"
            @click="handleSave"
          />
        </div>
      </div>
    </ThemeProvider>
  </VueFinalModal>
</template>

<style scoped>
  .vfm__overlay {
    background-color: rgba(0, 0, 0, 0.5);
  }
</style>
